import axios from 'axios'
import { useEffect, useMemo, useSyncExternalStore } from 'react'

export type ProductCategoryOption = { name: string }

const RETRY_AFTER_MS = 60_000

let categories: ProductCategoryOption[] = []
let options: { label: string; value: string }[] | null = null
let loading: Promise<void> | null = null
let loaded = false
let failedAt = 0
const listeners = new Set<() => void>()

export function productCategories(): ProductCategoryOption[] {
  return categories
}

export function productCategoryOptions(): { label: string; value: string }[] {
  options ??= categories.map(category => ({ label: category.name, value: category.name }))
  return options
}

export function loadProductCategories(): Promise<void> {
  if (loaded) return Promise.resolve()
  if (loading) return loading
  if (Date.now() - failedAt < RETRY_AFTER_MS) return Promise.resolve()
  loading = axios
    .get<{ fullName: string; isLeaf: boolean }[]>('/productCategories')
    .then(({ data }) => {
      const leaves = data.filter(category => category.isLeaf).map(category => ({ name: category.fullName }))
      if (leaves.length === 0) {
        failedAt = Date.now()
        return
      }
      categories = leaves
      options = null
      loaded = true
      listeners.forEach(listener => listener())
    })
    .catch(err => {
      failedAt = Date.now()
      console.error('Could not load product categories', err)
    })
    .finally(() => {
      loading = null
    })
  return loading
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useProductCategories(): ProductCategoryOption[] {
  useEffect(() => {
    void loadProductCategories()
  }, [])
  return useSyncExternalStore(subscribe, productCategories)
}

export function useProductCategoryOptions(): { label: string; value: string }[] {
  const categories = useProductCategories()
  return useMemo(() => categories.map(category => ({ label: category.name, value: category.name })), [categories])
}

export function productTypeForCategory(name: string | null | undefined): 'Product' | 'Service' | 'Maintenance' | null {
  if (!name) return null
  if (name === 'Maintenance' || name === 'Hardware : Warranty') return 'Maintenance'
  if (name.startsWith('Service')) return 'Service'
  return 'Product'
}

export const PRODUCT_CATEGORY_SOURCE_LABELS: Record<string, string> = {
  MANUAL: 'Set by user',
  PART: 'Saved for this part',
  TDSYNNEX: 'TD SYNNEX category',
  INGRAM: 'Ingram Micro category',
  DELL: 'Dell price list',
  CARAHSOFT: 'Carahsoft vendor',
  AI: 'AI classification',
  FALLBACK: 'Needs review',
  PENDING_AI: 'AI Classifying...',
}
